// import external libraries
import dayjs from 'dayjs';

// import constants
import {
  CARE_RECEIVERS_SESSION_STORAGE_KEYS,
  TabType
} from 'components/roster-portal/care-receivers/types';
import { BACKLOG_CATEGORIES } from './Backlog.consts';

// import types
import type { CareReceiverHandover } from 'components/roster-portal/care-receivers/types';
import type {
  BacklogCategoryFilter,
  PlanningEntityType
} from 'generated/index';
import type {
  BacklogEntityFilter,
  BacklogPlanUnitId,
  BacklogRequiredFilter,
  BacklogTaskCategory
} from './Backlog.types';

/**
 * A date as the legacy roster endpoints want it: a full ISO datetime.
 *
 * The employees-per-cost-centre and roster-functions calls are handled by the Groovy services, which parse
 * their dates with JsonDateTimeFormat. A date-only string is rejected there — the request fails with
 * `Unparseable date: "2026-08-17"` and a 500 — so this must not be confused with the `YYYY-MM-DD` the
 * takenlijst's own request uses. Both existing callers of those endpoints format it exactly this way.
 */
export const toLegacyApiDateTime = (date: Date): string =>
  dayjs(date).toISOString();

/**
 * The entity filters that narrow the list on screen: only those of the entity type being shown.
 *
 * The popover only offers the active view's filters, so only those may reach the backend — a pick left over
 * from another view would narrow the list with a filter the planner can neither see nor clear. The picks
 * themselves are kept, and apply again as soon as that view is chosen.
 */
export const entityFiltersForView = (
  entityFilters: BacklogEntityFilter[],
  rowEntityType: PlanningEntityType
): BacklogEntityFilter[] =>
  entityFilters.filter(
    ({ planningEntityType }) => planningEntityType === rowEntityType
  );

/**
 * The tile selection as the backend's category flags. Every category is named explicitly rather than
 * only the selected ones: an absent flag means "detect it" on the wire, so leaving one out would ask for
 * exactly the work the planner switched off.
 */
export const toCategoryFilter = (
  activeCategories: BacklogTaskCategory[]
): BacklogCategoryFilter =>
  Object.fromEntries(
    BACKLOG_CATEGORIES.map(({ category }) => [
      category,
      activeCategories.includes(category)
    ])
  ) as BacklogCategoryFilter;

/**
 * Hands the care receiver over to their own page through session storage and navigates to the
 * prescriptions tab, so a planner can see the prescription behind a deficit straight away.
 *
 * The tab travels in the hash — the Dojo host strips `&` params before matching the route — while the
 * care receiver travels in session storage: that page keeps its selection in state, and a label
 * containing a comma has no business in an unencoded hash param.
 */
export const navigateToCareReceiverPrescriptions = (
  careReceiverId: string,
  name: string
) => {
  const handover: CareReceiverHandover = { careReceiverId, name };

  window.sessionStorage.setItem(
    CARE_RECEIVERS_SESSION_STORAGE_KEYS.SELECTED_CARE_RECEIVER,
    JSON.stringify(handover)
  );

  window.location.href = `#roster,careReceivers&page=${TabType.PRESCRIPTIONS}`;
};

/**
 * The filter the view on screen cannot work without, or null when it has what it needs.
 *
 * Two views refuse to ask anything until they are scoped. The patient view reports on the care receivers the
 * planner names: without one it would fall back to every care receiver with a prescription in the period, the
 * heaviest question the backend can be asked, and rarely the one the planner meant. The employee view is the
 * same question over the tenant's employees, and its plan unit is what scopes it — it is also what the
 * employee and function lists are read from, so there is nothing to pick until it is set.
 *
 * One predicate for both the query's `enabled` and the page's empty state, so the page cannot end up
 * explaining that something is missing while the request goes out anyway.
 */
export const missingRequiredFilter = ({
  rowEntityType,
  entityFilters,
  planUnitIds
}: {
  rowEntityType: PlanningEntityType;
  entityFilters: BacklogEntityFilter[];
  planUnitIds: BacklogPlanUnitId[];
}): BacklogRequiredFilter | null => {
  if (
    rowEntityType === 'CLIENT' &&
    entityFiltersForView(entityFilters, rowEntityType).length === 0
  ) {
    return 'careReceiver';
  }
  if (rowEntityType === 'EMPLOYEE' && planUnitIds.length === 0) {
    return 'planUnit';
  }
  return null;
};
